import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import usePeriodFilter from "@/src/hooks/usePeriodFilter";

import { institutionSessionsByDayQuery } from "./edu.queries";
import { buildSessionsTimeline } from "./edu.utils";
import SessionAndViewAnalytics from "./SessionAndViewAnalytics";
import AnalyticsPeriodPicker from "../analytics/charts/AnalyticsPeriodPicker";
import TimelineQueryCard from "../analytics/charts/TimelineQueryCard";
import ViewsTable from "../analytics/userAnalytics/ViewsTable";

import type { Moment } from "moment";
import type { JSX } from "react";

import type { SessionsKey } from "./edu.utils";
import type { ChartSeries } from "../analytics/charts/charts.types";

interface AnalyticsInstitutionProps {
  institutionUuid: string;
  institutionCreationDate: Moment | null;
}

function AnalyticsInstitution({
  institutionUuid,
  institutionCreationDate,
}: AnalyticsInstitutionProps): JSX.Element {
  const { t } = useTranslation("editor-edu");
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { period, setPeriod } = usePeriodFilter();

  const seriesList = useMemo(
    (): ChartSeries<SessionsKey>[] => [
      { key: "sessions", label: t("sessionsSeries"), color: "var(--chart-1)" },
    ],
    [t],
  );

  const query = institutionSessionsByDayQuery(
    projectSlug,
    institutionUuid,
    period,
  );

  return (
    <div className="@container flex flex-col gap-6">
      <div className="flex justify-end">
        <AnalyticsPeriodPicker
          minDate={institutionCreationDate}
          onChange={setPeriod}
          period={period}
        />
      </div>
      <SessionAndViewAnalytics institutionUuid={institutionUuid} />
      <TimelineQueryCard
        buildTimeline={buildSessionsTimeline}
        endDate={period.endDate}
        query={query}
        seriesList={seriesList}
        startDate={period.startDate}
        title={t("sessionsViewTitle")}
      />
      <ViewsTable period={period} userUuid={institutionUuid} />
    </div>
  );
}

export default AnalyticsInstitution;
